import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Grid,
  TextField,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Paper,
  Alert,
  Tooltip,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Link as LinkIcon,
  Launch,
  Instagram,
  Facebook,
  Twitter,
  YouTube,
  Radio,
  Article,
  Assessment,
  Download,
  FilterList,
  DateRange,
  TrendingUp,
  Visibility,
  ThumbUp,
  Share,
  Comment,
  ExpandMore,
  Search
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axiosinstance from '../../utils/axiosConfig';

interface CampaignTrackingProps {
  resource: any;
  onUpdate: (updatedResource: any) => void;
}

interface PublicityItem {
  _id?: string;
  label: string;
  link: string;
  postedAt: Date;
  platform: 'instagram' | 'facebook' | 'press' | 'radio' | 'youtube' | 'tiktok' | 'other';
  notes?: string;
  // Extended fields for reporting
  engagementData?: {
    views?: number;
    likes?: number;
    shares?: number;
    comments?: number;
    reach?: number;
  };
  mediaType?: 'review' | 'interview' | 'mention' | 'feature' | 'user_generated' | 'sponsored';
  sentiment?: 'positive' | 'neutral' | 'negative';
  audienceReach?: 'local' | 'national' | 'international';
  keyQuotes?: string[];
  tags?: string[];
  reportingPriority?: 'high' | 'medium' | 'low';
}

const CampaignTrackingPhase: React.FC<CampaignTrackingProps> = ({ resource, onUpdate }) => {
  const [publicityTimeline, setPublicityTimeline] = useState<PublicityItem[]>(
    resource?.publicityTimeline || []
  );
  const [filteredTimeline, setFilteredTimeline] = useState<PublicityItem[]>(publicityTimeline);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterSentiment, setFilterSentiment] = useState('all');

  // Dialog states
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Form state
  const [newItem, setNewItem] = useState<PublicityItem>({
    label: '',
    link: '',
    postedAt: new Date(),
    platform: 'other',
    notes: '',
    mediaType: 'mention',
    sentiment: 'neutral',
    audienceReach: 'local',
    reportingPriority: 'medium',
    tags: [],
    keyQuotes: [],
    engagementData: {}
  });

  const [saving, setSaving] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Platform configurations with icons and colors
  const platformConfig = {
    instagram: { icon: Instagram, color: '#E4405F', label: 'Instagram' },
    facebook: { icon: Facebook, color: '#1877F2', label: 'Facebook' },
    twitter: { icon: Twitter, color: '#1DA1F2', label: 'Twitter/X' },
    youtube: { icon: YouTube, color: '#FF0000', label: 'YouTube' },
    tiktok: { icon: Share, color: '#000000', label: 'TikTok' },
    press: { icon: Article, color: '#424242', label: 'Press/Print' },
    radio: { icon: Radio, color: '#FF9800', label: 'Radio' },
    other: { icon: LinkIcon, color: '#757575', label: 'Other' }
  };

  const mediaTypes = [
    { value: 'review', label: 'Review' },
    { value: 'interview', label: 'Interview' },
    { value: 'mention', label: 'Mention' },
    { value: 'feature', label: 'Feature Story' },
    { value: 'user_generated', label: 'User Generated' },
    { value: 'sponsored', label: 'Sponsored Content' }
  ];

  const sentimentOptions = [
    { value: 'positive', label: 'Positive', color: 'success' },
    { value: 'neutral', label: 'Neutral', color: 'default' },
    { value: 'negative', label: 'Negative', color: 'error' }
  ];

  // Filter timeline based on search and filters
  useEffect(() => {
    let filtered = publicityTimeline;

    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.keyQuotes?.some(quote => quote.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filterPlatform !== 'all') {
      filtered = filtered.filter(item => item.platform === filterPlatform);
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.mediaType === filterType);
    }

    if (filterSentiment !== 'all') {
      filtered = filtered.filter(item => item.sentiment === filterSentiment);
    }

    setFilteredTimeline(filtered);
  }, [publicityTimeline, searchTerm, filterPlatform, filterType, filterSentiment]);

  // Calculate campaign analytics
  const getCampaignAnalytics = () => {
    const totalItems = publicityTimeline.length;
    const platformBreakdown = Object.keys(platformConfig).reduce((acc, platform) => {
      acc[platform] = publicityTimeline.filter(item => item.platform === platform).length;
      return acc;
    }, {} as Record<string, number>);

    const sentimentBreakdown = {
      positive: publicityTimeline.filter(item => item.sentiment === 'positive').length,
      neutral: publicityTimeline.filter(item => item.sentiment === 'neutral').length,
      negative: publicityTimeline.filter(item => item.sentiment === 'negative').length
    };

    const totalEngagement = publicityTimeline.reduce((total, item) => {
      const engagement = item.engagementData;
      return total + (engagement?.views || 0) + (engagement?.likes || 0) + (engagement?.shares || 0);
    }, 0);

    const highPriorityItems = publicityTimeline.filter(item => item.reportingPriority === 'high').length;
    const internationalReach = publicityTimeline.filter(item => item.audienceReach === 'international').length;
    const nationalReach = publicityTimeline.filter(item => item.audienceReach === 'national').length;

    return {
      totalItems,
      platformBreakdown,
      sentimentBreakdown,
      totalEngagement,
      highPriorityItems,
      internationalReach,
      nationalReach
    };
  };

  const analytics = getCampaignAnalytics();

  // Handle adding new publicity item
  const handleAddItem = () => {
    const updatedTimeline = [...publicityTimeline, { ...newItem }];
    setPublicityTimeline(updatedTimeline);
    setNewItem({
      label: '',
      link: '',
      postedAt: new Date(),
      platform: 'other',
      notes: '',
      mediaType: 'mention',
      sentiment: 'neutral',
      audienceReach: 'local',
      reportingPriority: 'medium',
      tags: [],
      keyQuotes: [],
      engagementData: {}
    });
    setAddItemDialogOpen(false);
    saveTimelineToResource(updatedTimeline);
  };

  // Handle deleting item
  const handleDeleteItem = (itemId: string) => {
    const updatedTimeline = publicityTimeline.filter(item => item._id !== itemId);
    setPublicityTimeline(updatedTimeline);
    saveTimelineToResource(updatedTimeline);
  };

  // Save timeline to resource
  const saveTimelineToResource = async (updatedTimeline: PublicityItem[]) => {
    setSaving(true);
    try {
      const updatedResource = { ...resource, publicityTimeline: updatedTimeline };
      await axiosinstance.put(`/api/resources/${resource._id}`, updatedResource);
      onUpdate(updatedResource);
    } catch (error) {
      console.error('Error saving timeline:', error);
    } finally {
      setSaving(false);
    }
  };

  // Generate report data for export
  const generateReportData = () => {
    return {
      resourceName: resource.name,
      performanceDate: resource.performances?.[0]?.eventDate,
      reportGenerated: new Date(),
      analytics,
      timeline: publicityTimeline.map(item => ({
        date: item.postedAt,
        platform: platformConfig[item.platform].label,
        type: item.mediaType,
        title: item.label,
        link: item.link,
        sentiment: item.sentiment,
        reach: item.audienceReach,
        engagement: item.engagementData,
        quotes: item.keyQuotes,
        notes: item.notes
      }))
    };
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Campaign Tracking & Results
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Track media coverage, social engagement, and campaign results for reporting
        </Typography>

        {/* Quick Analytics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{analytics.totalItems}</Typography>
              <Typography variant="caption">Total Coverage</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{analytics.sentimentBreakdown.positive}</Typography>
              <Typography variant="caption">Positive</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{analytics.totalEngagement.toLocaleString()}</Typography>
              <Typography variant="caption">Total Engagement</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{analytics.highPriorityItems}</Typography>
              <Typography variant="caption">Priority Items</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab label={`Timeline (${publicityTimeline.length})`} />
          <Tab label="Analytics" />
          <Tab label="Reporting" />
        </Tabs>
      </Card>

      {/* Timeline Tab */}
      {activeTab === 0 && (
        <>
          {/* Filters and Actions */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search coverage..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Platform</InputLabel>
                    <Select
                      value={filterPlatform}
                      onChange={(e) => setFilterPlatform(e.target.value)}
                      label="Platform"
                    >
                      <MenuItem value="all">All Platforms</MenuItem>
                      {Object.entries(platformConfig).map(([key, config]) => (
                        <MenuItem key={key} value={key}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <config.icon fontSize="small" />
                            {config.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {mediaTypes.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setAddItemDialogOpen(true)}
                      size="small"
                    >
                      Add Coverage
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Assessment />}
                      onClick={() => setReportDialogOpen(true)}
                      size="small"
                    >
                      Generate Report
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => {
                        const data = generateReportData();
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${resource.name}_campaign_report.json`;
                        a.click();
                      }}
                      size="small"
                    >
                      Export Data
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Timeline List */}
          <Card>
            <List>
              {filteredTimeline.map((item, index) => {
                const PlatformIcon = platformConfig[item.platform].icon;
                const platformColor = platformConfig[item.platform].color;
                const mediaTypeInfo = mediaTypes.find(t => t.value === item.mediaType);
                
                return (
                  <React.Fragment key={item._id || index}>
                    <ListItem>
                      <ListItemAvatar>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: platformColor + '20',
                            border: `2px solid ${platformColor}`
                          }}
                        >
                          <PlatformIcon sx={{ color: platformColor }} fontSize="small" />
                        </Box>
                      </ListItemAvatar>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1">{item.label}</Typography>
                            <Chip 
                              label={mediaTypeInfo?.label} 
                              size="small" 
                              variant="outlined"
                            />
                            <Chip 
                              label={item.sentiment} 
                              size="small" 
                              color={sentimentOptions.find(s => s.value === item.sentiment)?.color as any}
                            />
                            {item.reportingPriority === 'high' && (
                              <Chip label="Priority" size="small" color="warning" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {new Date(item.postedAt).toLocaleDateString()} • {item.audienceReach} reach
                            </Typography>
                            
                            {item.engagementData && Object.keys(item.engagementData).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                {item.engagementData.views && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Visibility fontSize="small" />
                                    <Typography variant="caption">{item.engagementData.views.toLocaleString()}</Typography>
                                  </Box>
                                )}
                                {item.engagementData.likes && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ThumbUp fontSize="small" />
                                    <Typography variant="caption">{item.engagementData.likes.toLocaleString()}</Typography>
                                  </Box>
                                )}
                                {item.engagementData.shares && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Share fontSize="small" />
                                    <Typography variant="caption">{item.engagementData.shares.toLocaleString()}</Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                            
                            {item.keyQuotes && item.keyQuotes.length > 0 && (
                              <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">Key Quote:</Typography>
                                <Typography variant="body2" fontStyle="italic">
                                  "{item.keyQuotes[0]}"
                                </Typography>
                              </Box>
                            )}
                            
                            {item.notes && (
                              <Typography variant="body2" color="text.secondary">
                                {item.notes}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Stack direction="row" spacing={1}>
                          <IconButton 
                            size="small"
                            onClick={() => window.open(item.link, '_blank')}
                            disabled={!item.link}
                          >
                            <Launch />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={() => {
                              console.log('Edit item:', item);
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteItem(item._id!)}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < filteredTimeline.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
            
            {filteredTimeline.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Assessment sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No campaign coverage found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {publicityTimeline.length === 0 ? 'Start tracking media coverage and social engagement' : 'Try adjusting your filters'}
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={() => setAddItemDialogOpen(true)}
                >
                  Add First Coverage
                </Button>
              </Box>
            )}
          </Card>
        </>
      )}

      {/* Analytics Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Platform Breakdown</Typography>
                {Object.entries(analytics.platformBreakdown).map(([platform, count]) => (
                  <Box key={platform} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.createElement(platformConfig[platform as keyof typeof platformConfig].icon, { 
                        fontSize: 'small', 
                        sx: { color: platformConfig[platform as keyof typeof platformConfig].color } 
                      })}
                      <Typography>{platformConfig[platform as keyof typeof platformConfig].label}</Typography>
                    </Box>
                    <Typography fontWeight="bold">{count}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Sentiment Analysis</Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="success.main">Positive</Typography>
                    <Typography fontWeight="bold">{analytics.sentimentBreakdown.positive}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Neutral</Typography>
                    <Typography fontWeight="bold">{analytics.sentimentBreakdown.neutral}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Negative</Typography>
                    <Typography fontWeight="bold">{analytics.sentimentBreakdown.negative}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {/* Reporting Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Campaign Report Summary</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ready-to-export data for Arts Council and annual reporting
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Executive Summary</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Campaign for <strong>{resource.name}</strong> generated <strong>{analytics.totalItems}</strong> pieces 
                  of media coverage with <strong>{analytics.totalEngagement.toLocaleString()}</strong> total engagement 
                  across all platforms.
                </Typography>
                <Typography paragraph>
                  <strong>{analytics.sentimentBreakdown.positive}</strong> positive mentions, 
                  <strong> {analytics.internationalReach}</strong> international reach items, 
                  and <strong>{analytics.highPriorityItems}</strong> high-priority coverage pieces 
                  suitable for grant reporting.
                </Typography>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Export Options</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Button 
                    variant="outlined" 
                    startIcon={<Download />}
                    onClick={() => {
                      const csvContent = [
                        ['Date', 'Platform', 'Type', 'Title', 'Sentiment', 'Reach', 'Link', 'Notes'],
                        ...publicityTimeline.map(item => [
                          new Date(item.postedAt).toLocaleDateString(),
                          platformConfig[item.platform].label,
                          mediaTypes.find(t => t.value === item.mediaType)?.label || '',
                          item.label,
                          item.sentiment,
                          item.audienceReach,
                          item.link,
                          item.notes || ''
                        ])
                      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
                      
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${resource.name}_campaign_report.csv`;
                      a.click();
                    }}
                  >
                    Export as CSV for Spreadsheet Analysis
                  </Button>
                  <Button variant="outlined" startIcon={<Download />}>
                    Export as PDF Report
                  </Button>
                  <Button variant="outlined" startIcon={<Download />}>
                    Export JSON Data for Database Import
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Add Coverage Dialog */}
      <Dialog open={addItemDialogOpen} onClose={() => setAddItemDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Campaign Coverage</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Title/Description"
              value={newItem.label}
              onChange={(e) => setNewItem(prev => ({ ...prev, label: e.target.value }))}
              placeholder="e.g., Irish Times Review, Artist Instagram Story"
              required
            />
            
            <TextField
              fullWidth
              label="Link/URL"
              value={newItem.link}
              onChange={(e) => setNewItem(prev => ({ ...prev, link: e.target.value }))}
              placeholder="https://..."
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={newItem.platform}
                    onChange={(e) => setNewItem(prev => ({ ...prev, platform: e.target.value as any }))}
                    label="Platform"
                  >
                    {Object.entries(platformConfig).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <config.icon fontSize="small" />
                          {config.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Media Type</InputLabel>
                  <Select
                    value={newItem.mediaType}
                    onChange={(e) => setNewItem(prev => ({ ...prev, mediaType: e.target.value as any }))}
                    label="Media Type"
                  >
                    {mediaTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Sentiment</InputLabel>
                  <Select
                    value={newItem.sentiment}
                    onChange={(e) => setNewItem(prev => ({ ...prev, sentiment: e.target.value as any }))}
                    label="Sentiment"
                  >
                    {sentimentOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Audience Reach</InputLabel>
                  <Select
                    value={newItem.audienceReach}
                    onChange={(e) => setNewItem(prev => ({ ...prev, audienceReach: e.target.value as any }))}
                    label="Audience Reach"
                  >
                    <MenuItem value="local">Local</MenuItem>
                    <MenuItem value="national">National</MenuItem>
                    <MenuItem value="international">International</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={newItem.reportingPriority}
                    onChange={(e) => setNewItem(prev => ({ ...prev, reportingPriority: e.target.value as any }))}
                    label="Priority"
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High (Arts Council Report)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Posted Date"
                value={newItem.postedAt}
                onChange={(date) => setNewItem(prev => ({ ...prev, postedAt: date || new Date() }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            
            {/* Engagement Data */}
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Engagement Metrics (Optional)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Views"
                  type="number"
                  value={newItem.engagementData?.views || ''}
                  onChange={(e) => setNewItem(prev => ({
                    ...prev,
                    engagementData: { ...prev.engagementData, views: parseInt(e.target.value) || 0 }
                  }))}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Likes"
                  type="number"
                  value={newItem.engagementData?.likes || ''}
                  onChange={(e) => setNewItem(prev => ({
                    ...prev,
                    engagementData: { ...prev.engagementData, likes: parseInt(e.target.value) || 0 }
                  }))}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Shares"
                  type="number"
                  value={newItem.engagementData?.shares || ''}
                  onChange={(e) => setNewItem(prev => ({
                    ...prev,
                    engagementData: { ...prev.engagementData, shares: parseInt(e.target.value) || 0 }
                  }))}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Comments"
                  type="number"
                  value={newItem.engagementData?.comments || ''}
                  onChange={(e) => setNewItem(prev => ({
                    ...prev,
                    engagementData: { ...prev.engagementData, comments: parseInt(e.target.value) || 0 }
                  }))}
                />
              </Grid>
            </Grid>
            
            <TextField
              fullWidth
              label="Key Quote (for reporting)"
              multiline
              rows={2}
              value={newItem.keyQuotes?.[0] || ''}
              onChange={(e) => setNewItem(prev => ({ ...prev, keyQuotes: e.target.value ? [e.target.value] : [] }))}
              placeholder="Extract key quote for Arts Council reports..."
            />
            
            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={newItem.tags?.join(', ') || ''}
              onChange={(e) => setNewItem(prev => ({ 
                ...prev, 
                tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
              }))}
              placeholder="folk, traditional, dublin, festival"
            />
            
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={newItem.notes}
              onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional context, journalist contact info, follow-up notes..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddItem} 
            variant="contained"
            disabled={!newItem.label}
          >
            Add Coverage
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Generation Dialog */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Campaign Report - {resource.name}</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell>Reach</TableCell>
                  <TableCell>Engagement</TableCell>
                  <TableCell>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {publicityTimeline
                  .sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime())
                  .map((item) => (
                  <TableRow key={item._id}>
                    <TableCell>{new Date(item.postedAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {React.createElement(platformConfig[item.platform].icon, { 
                          fontSize: 'small',
                          sx: { color: platformConfig[item.platform].color }
                        })}
                        {platformConfig[item.platform].label}
                      </Box>
                    </TableCell>
                    <TableCell>{mediaTypes.find(t => t.value === item.mediaType)?.label}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{item.label}</Typography>
                        {item.link && (
                          <Button 
                            size="small" 
                            startIcon={<Launch />}
                            onClick={() => window.open(item.link, '_blank')}
                          >
                            View
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.sentiment} 
                        size="small" 
                        color={sentimentOptions.find(s => s.value === item.sentiment)?.color as any}
                      />
                    </TableCell>
                    <TableCell>{item.audienceReach}</TableCell>
                    <TableCell>
                      {item.engagementData && (
                        <Typography variant="caption">
                          {Object.values(item.engagementData).reduce((a, b) => (a || 0) + (b || 0), 0).toLocaleString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.reportingPriority === 'high' && (
                        <Chip label="High" size="small" color="warning" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>Arts Council Summary</Typography>
            <Typography paragraph>
              <strong>Total Media Coverage:</strong> {analytics.totalItems} items
            </Typography>
            <Typography paragraph>
              <strong>Sentiment Breakdown:</strong> {analytics.sentimentBreakdown.positive} positive, 
              {analytics.sentimentBreakdown.neutral} neutral, {analytics.sentimentBreakdown.negative} negative
            </Typography>
            <Typography paragraph>
              <strong>Reach:</strong> {analytics.internationalReach} international, 
              {analytics.nationalReach} national coverage pieces
            </Typography>
            <Typography paragraph>
              <strong>Total Social Engagement:</strong> {analytics.totalEngagement.toLocaleString()} 
              (views, likes, shares, comments combined)
            </Typography>
            <Typography paragraph>
              <strong>High Priority Items:</strong> {analytics.highPriorityItems} pieces 
              suitable for grant reporting and impact measurement
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained"
            startIcon={<Download />}
            onClick={() => {
              const csvContent = [
                ['Date', 'Platform', 'Type', 'Title', 'Sentiment', 'Reach', 'Link', 'Notes'],
                ...publicityTimeline.map(item => [
                  new Date(item.postedAt).toLocaleDateString(),
                  platformConfig[item.platform].label,
                  mediaTypes.find(t => t.value === item.mediaType)?.label || '',
                  item.label,
                  item.sentiment,
                  item.audienceReach,
                  item.link,
                  item.notes || ''
                ])
              ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
              
              const blob = new Blob([csvContent], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${resource.name}_campaign_report.csv`;
              a.click();
            }}
          >
            Export Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save indicator */}
      {saving && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
          <Alert severity="info">
            Saving campaign data...
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default CampaignTrackingPhase;